import {DOCUMENT} from '@angular/common';
import {Injectable, Signal, computed, inject, signal} from '@angular/core';

import {DARK_CLASS, DEFAULT_THEME_MODE, ResolvedTheme, THEME_STORAGE_KEY, ThemeMode, isThemeMode} from './theme.const';

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

@Injectable({providedIn: 'root'})
export class ThemeStore {
  private readonly document = inject(DOCUMENT);

  private readonly _mode = signal<ThemeMode>(DEFAULT_THEME_MODE);
  readonly mode: Signal<ThemeMode> = this._mode.asReadonly();

  /**
   * OS preference as a signal, so `resolved` recomputes when it flips. Reading
   * `matchMedia` straight from the computed would memoize the first answer forever.
   */
  private readonly _systemDark = signal(false);

  readonly resolved = computed<ResolvedTheme>(() => {
    const mode = this._mode();
    if (mode !== 'system') return mode;
    return this._systemDark() ? 'dark' : 'light';
  });

  initialize(): void {
    const stored = this.readStored();
    this._mode.set(stored ?? DEFAULT_THEME_MODE);
    this.watchSystem();
    this.apply();
  }

  setMode(mode: ThemeMode): void {
    this._mode.set(mode);
    this.persist(mode);
    this.syncSystemDark();
    this.apply();
  }

  private apply(): void {
    const html = this.document.documentElement;
    if (this.resolved() === 'dark') {
      html.classList.add(DARK_CLASS);
    } else {
      html.classList.remove(DARK_CLASS);
    }
  }

  private watchSystem(): void {
    const mq = this.mediaQuery();
    if (!mq) return;
    this._systemDark.set(mq.matches);
    mq.addEventListener('change', () => {
      this._systemDark.set(mq.matches);
      this.apply();
    });
  }

  /** Re-read the OS preference when the user opts back into following it. */
  private syncSystemDark(): void {
    const mq = this.mediaQuery();
    if (mq) this._systemDark.set(mq.matches);
  }

  private mediaQuery(): MediaQueryList | undefined {
    return this.document.defaultView?.matchMedia(SYSTEM_DARK_QUERY);
  }

  private readStored(): ThemeMode | null {
    const value = this.document.defaultView?.localStorage.getItem(THEME_STORAGE_KEY) ?? null;
    return isThemeMode(value) ? value : null;
  }

  private persist(mode: ThemeMode): void {
    this.document.defaultView?.localStorage.setItem(THEME_STORAGE_KEY, mode);
  }
}
